import { QuestionCircleOutlined } from "@ant-design/icons";
import { Space, Tooltip, Typography } from "antd";
import { Trans } from "react-i18next";
import ProductParameter from "components/_storybooks/ProductParameter";
import CustomTable from "components/CustomTable";
import {
  PROMOTION_CAMPAIGN_STATUS,
  SELLER_USER_SEGMENT_TYPE,
  isProductPromoProgramBudgetRequired,
} from "features/Campaign/constants";
import {
  getProductPromoTierBudgetValue,
  resolveProductPromoSellerSegmentType,
} from "features/Campaign/utils/productPromoTargetCustomer";
import { t } from "i18next";
import { formatCurrency } from "utils/helperFuncs";
import ProgressBarSoldQuantity from "../ProgressBarSoldQuantity";
import "./index.scss";

const SEGMENT_TYPES_WITHOUT_CONDITION_FOR_REFERENCE = [
  SELLER_USER_SEGMENT_TYPE.NO_ORDER,
  SELLER_USER_SEGMENT_TYPE.NOT_PURCHASED_PRODUCT,
];

function ProductPromoDetail({ data }) {
  const dataSource = data.promotionCampaignTierGroups?.[0]?.promotionCampaignTiers;
  const sellerUserSegmentType = resolveProductPromoSellerSegmentType(data?.sellerSegment?.type);
  const showConditionForReference =
    data?.status === PROMOTION_CAMPAIGN_STATUS.ON_GOING &&
    !SEGMENT_TYPES_WITHOUT_CONDITION_FOR_REFERENCE.includes(sellerUserSegmentType);

  const priceChildrenFinal = (price, isDiscountPercentage, discountAmount) => {
    return (
      <span>
        <div style={{ fontSize: "12px", textDecorationLine: "line-through" }}>{formatCurrency(price)}</div>
        <div>
          {isDiscountPercentage
            ? formatCurrency(price * (1 - discountAmount / 100))
            : formatCurrency(price - discountAmount)}
        </div>
      </span>
    );
  };

  const columnsDataSelected = [
    {
      key: "stt",
      title: <span>STT</span>,
      width: 60,
      align: "center",
      render: (_, record, index) => index + 1,
    },
    {
      key: "productInfo",
      title: "Thông số",
      width: 300,
      render: (_, record) => {
        const product = record?.conditionProductGroups?.[0]?.conditionPromotionProducts?.[0]?.product || {};
        return <ProductParameter parameter={product} />;
      },
    },
    {
      key: "discount",
      title: (
        <Space>
          <Typography.Text style={{ color: "#5B6673" }}>{t("campaign.productPromo.discount")}</Typography.Text>
          <Tooltip title={t("campaign.productPromo.discountTooltip")}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      width: 150,
      align: "right",
      render: (_, record, index) => {
        return record?.discountType === "DISCOUNT_MONEY"
          ? formatCurrency(record?.discountAmount)
          : `${record?.discountAmount}%`;
      },
    },
    {
      key: "buyLimit",
      title: (
        <Space>
          <Typography.Text style={{ color: "#5B6673", textJustify: "inter-word", textAlign: "left" }}>
            {t("campaign.productPromo.buyLimitProduct")}
          </Typography.Text>
          <Tooltip title={t("campaign.productPromo.buyLimitProductTooltip")}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      width: 200,
      align: "center",
      render: (_, record, index) => {
        return record?.maxQuantityPerSeller ?? "Không giới hạn";
      },
    },
    {
      key: "budget",
      title: (
        <Space>
          <Typography.Text style={{ color: "#5B6673" }}>
            {isProductPromoProgramBudgetRequired(sellerUserSegmentType)
              ? t("campaign.productPromo.budget")
              : t("campaign.productPromo.budgetAllSegment")}
          </Typography.Text>
          <Tooltip title={t("campaign.productPromo.budgetTooltip")}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      width: 150,
      align: "right",
      render: (_, record, index) => {
        return formatCurrency(getProductPromoTierBudgetValue(record, sellerUserSegmentType));
      },
    },
    {
      key: "priceVat",
      title: (
        <Space>
          <Typography.Text style={{ color: "#5B6673" }}>{t("campaign.productPromo.tempoPromoPrice")}</Typography.Text>
          <Tooltip title={t("campaign.productPromo.tempoPromoPriceTooltip")}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      width: 150,
      align: "center",
      render: (_, record) => {
        const product = record?.conditionProductGroups?.[0]?.conditionPromotionProducts?.[0]?.product || {};
        return priceChildrenFinal(
          product?.productPrices?.priceVat,
          record?.discountType === "DISCOUNT_PERCENTAGE",
          record?.discountAmount
        );
      },
    },

    ...(showConditionForReference
      ? [
          {
            key: "conditionForReference",
            title: (
              <Space>
                <Typography.Text style={{ color: "#5B6673" }}>
                  {t("campaign.productPromo.conditionForReference")}
                </Typography.Text>
                <Tooltip title={t("campaign.productPromo.conditionForReferenceTooltip")}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            ),
            width: 170,
            align: "center",
            fixed: "right",
            render: (_, record, index) => {
              const soldQuantityOfTier = {
                soldQuantity: record?.usedQuantity || 0,
                totalQuantity: record?.totalQuantity || 0,
              };
              return <ProgressBarSoldQuantity soldQuantityOfTier={soldQuantityOfTier} />;
            },
          },
        ]
      : []),
  ];

  const showSumBudget = !isProductPromoProgramBudgetRequired(sellerUserSegmentType);
  const sumBudget = data?.promotionCampaignTierGroups?.reduce(
    (sum, group) =>
      sum +
      (group?.promotionCampaignTiers?.reduce(
        (tierSum, tier) => tierSum + (getProductPromoTierBudgetValue(tier, sellerUserSegmentType) || 0),
        0
      ) || 0),
    0
  );

  return (
    <div>
      <div style={{ padding: "0 0 12px 0" }}>
        <Space direction="vertical" size={4}>
          <Typography.Text>
            <Trans
              i18nKey="campaign.productPromo.sumProductPromo"
              values={{
                sumProduct:
                  data?.promotionCampaignTierGroups?.reduce(
                    (sum, group) =>
                      sum +
                      (group?.promotionCampaignTiers?.reduce(
                        (tierSum, tier) => tierSum + (tier?.conditionProductGroups?.length || 0),
                        0
                      ) || 0),
                    0
                  ) || 0,
              }}
              components={{ style: <Typography.Text style={{ color: "#2246DC" }} /> }}
            />
          </Typography.Text>
          {showSumBudget && (
            <Typography.Text>
              <Trans
                i18nKey="campaign.productPromo.sumBudgetDetail"
                values={{ sumBudget: formatCurrency(sumBudget || 0) }}
                components={{ style: <Typography.Text style={{ color: "#2246DC" }} /> }}
              />
            </Typography.Text>
          )}
        </Space>
      </div>

      <CustomTable
        className="product-promo-detail-table"
        columns={columnsDataSelected}
        dataSource={dataSource}
        rowKey={(record) => record?.id ?? record?.tierId ?? JSON.stringify(record)}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
}

export default ProductPromoDetail;
